<template>
  <el-container v-if="$store.state.isAuthenticated" class="app-container">
    <el-header>
      <div class="header-content">
        <h1>南意秋棠后台管理系统</h1>
        <div class="header-right">
          <el-button 
            type="primary" 
            plain 
            @click="goToFrontend"
            class="frontend-link-btn"
          >
            <el-icon><View /></el-icon>
            布料展示前台
          </el-button>
          <el-dropdown @command="handleCommand" trigger="click">
            <span class="user-dropdown">
              <el-avatar :size="32" icon="el-icon-user" />
              <span class="username">{{ username }}</span>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><user /></el-icon>
                  个人资料
                </el-dropdown-item>
                <el-dropdown-item command="changePassword">
                  <el-icon><key /></el-icon>
                  修改密码
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <el-icon><switch-button /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </el-header>
    
    <el-container class="body-wrap">
      <el-aside width="200px" class="app-aside">
        <el-menu
          :default-active="activeMenu"
          router
          class="aside-menu"
        >
          <el-menu-item index="/">
            <span>布料管理</span>
          </el-menu-item>
          <el-menu-item index="/conversion">
            <span>转化分析</span>
          </el-menu-item>
          <el-menu-item-group title="返图欣赏">
            <el-menu-item index="/gallery/notes">
              <span>笔记管理</span>
            </el-menu-item>
            <el-menu-item index="/gallery/authors">
              <span>作者管理</span>
            </el-menu-item>
          </el-menu-item-group>
        </el-menu>
      </el-aside>
      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>

    <el-footer class="app-footer">
      <div class="footer-content">
        <span>{{ copyrightText }}</span>
      </div>
    </el-footer>

    <!-- 修改密码对话框 -->
    <el-dialog
      v-model="changePasswordVisible"
      title="修改密码"
      width="400px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="passwordFormRef"
        :model="passwordForm"
        :rules="passwordRules"
        label-width="100px"
      >
        <el-form-item label="原密码" prop="oldPassword">
          <el-input
            v-model="passwordForm.oldPassword"
            type="password"
            show-password
            placeholder="请输入原密码"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            show-password
            placeholder="请输入新密码"
          />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            show-password
            placeholder="请再次输入新密码"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="changePasswordVisible = false">取消</el-button>
          <el-button type="primary" @click="handleChangePassword" :loading="changing">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 个人资料对话框 -->
    <el-dialog
      v-model="profileVisible"
      title="个人资料"
      width="400px"
    >
      <el-form
        ref="profileFormRef"
        :model="profileForm"
        label-width="100px"
      >
        <el-form-item label="用户名">
          <el-input v-model="profileForm.username" disabled />
        </el-form-item>
        <el-form-item label="创建时间">
          <el-input v-model="profileForm.createdAt" disabled />
        </el-form-item>
        <el-form-item label="最后登录">
          <el-input v-model="profileForm.lastLogin" disabled />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="profileVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>
  </el-container>
  
  <router-view v-else />
</template>

<script>
import { defineComponent, ref, reactive, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Key, SwitchButton, View } from '@element-plus/icons-vue'

export default defineComponent({
  name: 'App',
  components: {
    User,
    Key,
    SwitchButton,
    View
  },
  
  setup() {
    const store = useStore()
    const router = useRouter()
    const passwordFormRef = ref(null)
    const profileFormRef = ref(null)
    const changePasswordVisible = ref(false)
    const profileVisible = ref(false)
    const changing = ref(false)

    const username = computed(() => store.state.user?.username || 'admin123')
    const activeMenu = computed(() => router.currentRoute.value.path || '/')

    // 版权信息
    const copyrightText = computed(() => {
      return process.env.VUE_APP_COPYRIGHT || '备案号：浙ICP备2024081979号 | 版权所有，nanyiqiutang.cn南意秋棠'
    })

    const passwordForm = reactive({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    })

    const profileForm = reactive({
      username: username.value,
      createdAt: '2024-01-01 00:00:00',
      lastLogin: '2024-01-01 00:00:00'
    })

    const passwordRules = {
      oldPassword: [
        { required: true, message: '请输入原密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能小于6位', trigger: 'blur' }
      ],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能小于6位', trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value !== passwordForm.newPassword) {
              callback(new Error('两次输入的密码不一致'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ]
    }

    const handleCommand = (command) => {
      switch (command) {
        case 'profile':
          profileVisible.value = true
          break
        case 'changePassword':
          changePasswordVisible.value = true
          break
        case 'logout':
          handleLogout()
          break
      }
    }

    // 跳转到前台展示页面
    const goToFrontend = () => {
      window.open('https://products.nanyiqiutang.cn', '_blank')
    }

    const handleChangePassword = async () => {
      if (!passwordFormRef.value) return

      await passwordFormRef.value.validate(async (valid) => {
        if (valid) {
          changing.value = true
          try {
            await store.dispatch('changePassword', {
              oldPassword: passwordForm.oldPassword,
              newPassword: passwordForm.newPassword
            })
            ElMessage.success('密码修改成功')
            changePasswordVisible.value = false
            // 重置表单
            passwordForm.oldPassword = ''
            passwordForm.newPassword = ''
            passwordForm.confirmPassword = ''
          } catch (error) {
            ElMessage.error(error.response?.data?.message || '密码修改失败')
          } finally {
            changing.value = false
          }
        }
      })
    }

    const handleLogout = async () => {
      try {
        await store.dispatch('logout')
        router.push('/login')
        ElMessage.success('退出登录成功')
      } catch (error) {
        ElMessage.error('退出登录失败')
      }
    }

    return {
      passwordFormRef,
      profileFormRef,
      passwordForm,
      profileForm,
      passwordRules,
      changePasswordVisible,
      profileVisible,
      username,
      copyrightText,
      activeMenu,
      changing,
      handleCommand,
      handleChangePassword,
      handleLogout,
      goToFrontend
    }
  }
})
</script>

<style>
body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
}

.el-header {
  background-color: #409EFF;
  color: white;
  line-height: 60px;
  padding: 0 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
}

.header-content h1 {
  margin: 0;
  font-size: 18px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.frontend-link-btn {
  margin-right: 10px;
}

.user-dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: white;
}

.username {
  margin-left: 8px;
  font-size: 14px;
}

.body-wrap {
  min-height: calc(100vh - 120px);
}
.app-aside {
  background: #fff;
  border-right: 1px solid #ebeef5;
}
.aside-menu {
  border-right: none;
  height: 100%;
}
.el-main {
  background-color: #f5f7fa;
  min-height: calc(100vh - 120px);
}

.el-footer {
  background-color: #f5f7fa;
  line-height: 60px;
  padding: 0 20px;
}

.footer-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

:deep(.el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.el-avatar) {
  background-color: rgba(255, 255, 255, 0.2);
}
</style> 